export type ConnectionPreflightState = 'ok' | 'attention' | 'blocked';

export interface ConnectionPreflightEnvironment {
  online: boolean;
  secureContext: boolean;
  hasWebRtc: boolean;
  hasClipboard: boolean;
}

export interface ConnectionPreflightCheck {
  id: 'browser' | 'network' | 'clipboard' | 'environment';
  label: string;
  detail: string;
  state: ConnectionPreflightState;
}

export interface ConnectionPreflightResult {
  state: ConnectionPreflightState;
  summary: string;
  canConnect: boolean;
  checks: ConnectionPreflightCheck[];
}

/**
 * 连接前能力判断只读取当前环境，不主动申请任何系统权限。
 * 剪贴板或非 HTTPS 环境受限时仍允许远程控制，只把影响告诉用户。
 */
export function evaluateConnectionPreflight(
  environment: ConnectionPreflightEnvironment,
): ConnectionPreflightResult {
  const checks: ConnectionPreflightCheck[] = [
    environment.hasWebRtc
      ? {
          id: 'browser',
          label: '浏览器支持',
          detail: '可以建立远程连接',
          state: 'ok',
        }
      : {
          id: 'browser',
          label: '浏览器支持',
          detail: '当前浏览器无法建立实时连接，请更换最新版浏览器',
          state: 'blocked',
        },
    environment.online
      ? {
          id: 'network',
          label: '网络状态',
          detail: '网络已连接',
          state: 'ok',
        }
      : {
          id: 'network',
          label: '网络状态',
          detail: '网络暂时不可用，恢复后可以继续连接',
          state: 'blocked',
        },
    environment.hasClipboard
      ? {
          id: 'clipboard',
          label: '剪贴板',
          detail: '连接后可以同步复制内容',
          state: 'ok',
        }
      : {
          id: 'clipboard',
          label: '剪贴板',
          detail: '可以连接，复制内容时可能需要手动确认',
          state: 'attention',
        },
    environment.secureContext
      ? {
          id: 'environment',
          label: '连接环境',
          detail: '当前环境完整支持连接功能',
          state: 'ok',
        }
      : {
          id: 'environment',
          label: '连接环境',
          detail: '当前环境可以连接，但部分浏览器能力可能受限',
          state: 'attention',
        },
  ];
  const blocked = checks.some((check) => check.state === 'blocked');
  const attention = checks.some((check) => check.state === 'attention');
  return {
    state: blocked ? 'blocked' : attention ? 'attention' : 'ok',
    summary: blocked ? '暂时无法连接' : attention ? '可以连接，但部分功能受限' : '可以开始连接',
    canConnect: !blocked,
    checks,
  };
}
